import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Valida CNPJ no padrão alfanumérico da Receita Federal/Serpro:
 * 12 caracteres alfanuméricos (A-Z e 0-9) + 2 dígitos verificadores numéricos.
 *
 * O valor de cálculo de cada caractere é o seu código ASCII menos 48
 * ('0'..'9' -> 0..9, 'A' -> 17, ..., 'Z' -> 42), e os dígitos verificadores
 * usam módulo 11 com pesos distribuídos de 2 a 9 (da direita para a esquerda).
 */
export function isValidCnpjAlphanumeric(value: string): boolean {
  // Remove máscara (pontos, barra, hífen, espaços) e normaliza para maiúsculo.
  const cnpj = (value ?? '').toString().toUpperCase().replace(/[^0-9A-Z]/g, '');

  // 12 caracteres alfanuméricos + 2 dígitos verificadores numéricos.
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj)) {
    return false;
  }

  // Rejeita sequências com os 12 caracteres-base todos iguais
  // (ex.: "00000000000000", "AAAAAAAAAAAA00"): passam no módulo 11, mas são inválidas.
  if (/^(.)\1{11}/.test(cnpj)) {
    return false;
  }

  const charValue = (c: string): number => c.charCodeAt(0) - 48;

  const calcDigit = (chars: string, weights: number[]): number => {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) {
      sum += charValue(chars[i]) * weights[i];
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const base = cnpj.slice(0, 12);
  const firstDigit = calcDigit(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const secondDigit = calcDigit(base + firstDigit, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

  return cnpj[12] === String(firstDigit) && cnpj[13] === String(secondDigit);
}

export function cnpjAlphanumeric(): ValidatorFn {
  return (control: AbstractControl<string>): ValidationErrors | null => {
    const value = control.value;

    // Não valida se vazio (Validators.required cuida da obrigatoriedade).
    if (!value) {
      return null;
    }

    return isValidCnpjAlphanumeric(value) ? null : { cnpjInvalid: true };
  };
}
